import { useTenantsListApi } from '@/features/superAdmin/features/tenants/hooks/useTenantsListApi';
import { cn } from '@/lib/utils';
import { FormInputProps } from '@/@types/form.types';
import { Controller } from 'react-hook-form';
import { CreateUpdateUserTenantRoleDto } from '@/features/superAdmin/features/users/types/superAdmin-users-dto';
import UserTenantRolesPicker from '@/features/superAdmin/features/users/components/UserTenantRolesPicker';

type Props<T extends {}> = {
    label?: string;
} & FormInputProps<T>;

const UserTenantRoles = <T extends {}>({
    control,
    fieldName,
    label,
    containerClassName,
}: Props<T>) => {
    const { tenantsListData } = useTenantsListApi();

    return (
        <Controller
            name={fieldName}
            control={control}
            render={({ field: { value, onChange }, fieldState, formState }) => {
                const tenantRolesArray: CreateUpdateUserTenantRoleDto[] = Array.isArray(value)
                    ? value
                    : [];

                const tenantsList = tenantsListData?.items?.map((tenant) => {
                    const role = tenantRolesArray?.find((role) => role.tenantId === tenant.id);

                    return role ? { ...tenant, roleId: role.roleId } : { ...tenant, roleId: '' };
                });

                const handleChangeRole = (tenantId: string, roleId?: string) => {
                    let updatedTenantRolesArr;

                    if (roleId === '') {
                        updatedTenantRolesArr = tenantRolesArray.filter(
                            (tenant) => tenant.tenantId !== tenantId,
                        );
                    } else if (tenantRolesArray.some((tenant) => tenant.tenantId === tenantId)) {
                        updatedTenantRolesArr = tenantRolesArray.map((tenant) =>
                            tenant.tenantId === tenantId && tenant.roleId !== roleId
                                ? { ...tenant, roleId }
                                : tenant,
                        );
                    } else {
                        updatedTenantRolesArr = [...tenantRolesArray, { tenantId, roleId }];
                    }

                    onChange(updatedTenantRolesArr);
                };

                return (
                    <div
                        className={cn(
                            'w-full flex flex-col gap-1.5 select-none',
                            containerClassName,
                        )}
                    >
                        {label && <h4 className="text-sm font-medium">{label}</h4>}
                        <div className="max-h-71 flex flex-col gap-4 overflow-y-auto">
                            {tenantsList?.map(({ id, name, roleId }) => {
                                return (
                                    <div
                                        key={id}
                                        className="flex items-center justify-between gap-4"
                                    >
                                        <span>{name || '-'}</span>
                                        <UserTenantRolesPicker
                                            roleId={roleId}
                                            onChange={(roleId) => handleChangeRole(id, roleId)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }}
        />
    );
};

export default UserTenantRoles;
